import React, { useEffect, useMemo, useState } from 'react';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import { Avatar, Rating } from '@mui/material';
import moment from 'moment';
import { Checkbox } from 'primereact/checkbox';
import { InputTextarea } from 'primereact/inputtextarea';
import { RadioButton } from 'primereact/radiobutton';
import { Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CustomTooltips } from '../../../../components';
import { IMAGE_URL, VENDOR_IMAGE_URL } from '../../../../constants/Config';
import { capitalizeFirstLetter } from '../../../../utils/CapitalizeFirstLetterUtils';
import { truncateString } from '../../../../utils/TruncateStringUtils';
import { getUserInfo } from '../../../../utils/UserInfoUtils';
import { ProofreaderRoleEditorSubmitRequest, vendorNameListRequest } from '../../Editor/actions';
import VendorSelectWithSearch from '../../Editor/component/VendorSelectWithSearch';
import {
  downloadLegalDocumentRequest,
  uploadLegalDocumentFailure,
  uploadLegalDocumentRequest,
} from '../actions/settingsActions';
import { trackEvent } from '../../../../utils/ga';
import CustomTypography from '../../../../components/mui/CustomTypography';
import { formatDateWithMilliseconds } from '../../../../constants/CurrentTimeFormet';

const ProofreaderSubmitModal = ({
  editorDetailsData,
  vendorDetailData,
  submitShow,
  handleSubmitCloseModal,
  setFilterApiCall,
}) => {
  const dispatch = useDispatch();
  const [rateExplanantionValue, setRateExplanantionValue] = useState('');
  const [reworkNote, setReworkNote] = useState('');
  const isConfirmButtonLoading = useSelector((state) => state?.proofreaderRoleEditorSubmit?.loading);
  const [valueRating, setValueRating] = useState(vendorDetailData?.Rate || 0);
  const [page, setPage] = useState('Vendor Rating');
  const [valueRework, setValueRework] = useState('');
  const [valueSendFile, setValueSendFile] = useState('');
  const [payVendor, setPayVendor] = useState('');
  const [blockVendor, setBlockVendor] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [payOriginalVendor, setPayOriginalVendor] = useState(false);
  const [hour, setHour] = useState(null);
  const [selectedNewVendor, setSelectedNewVendor] = useState(null);
  // Add this selector to get the transcript data from Redux state
  const { isUploadedLegalDocument } = useSelector((state) => state?.settings || {});
  const slateDataState = useSelector((state) => state?.slateData || {});
  const legalTemplateFormFieldDetails = useSelector((state) => state?.settings?.legalTemplateFormFieldDetails);
  const fieldsData = legalTemplateFormFieldDetails?.FormField?.Fields || [];
  const initialValues = useMemo(() => {
    const values = {};
    const fv = legalTemplateFormFieldDetails?.FormDetail?.FieldValues || [];

    const valueById = fv.reduce((acc, item) => {
      if (item.FieldId) {
        acc[item.FieldId.toLowerCase()] = item.FieldValue;
      }
      return acc;
    }, {});

    fieldsData.forEach((field) => {
      if (field.FieldType === 'Group' && Array.isArray(field.Children)) {
        field.Children.forEach((arrayField) => {
          const raw = valueById[field.FieldId?.toLowerCase()];
          if (raw && typeof raw === 'object' && raw[arrayField.FieldName]) {
            values[arrayField.DisplayName] = raw[arrayField.FieldName].map((item) => {
              const obj = {};
              (arrayField.Children || []).forEach((child) => {
                obj[child.DisplayName] = item[child.FieldName] ?? '';
              });
              return obj;
            });
          } else {
            values[arrayField.DisplayName] = [
              (arrayField.Children || []).reduce((acc, child) => {
                acc[child.DisplayName] = '';
                return acc;
              }, {}),
            ];
          }
        });
      } else {
        values[field.DisplayName] = valueById[field.FieldId?.toLowerCase()] ?? '';
      }
    });

    return values;
  }, [fieldsData, legalTemplateFormFieldDetails]);
  const { data: transcriptData = [] } = slateDataState;
  const { id } = useParams();
  const userInfo = getUserInfo();

  useEffect(() => {
    if (editorDetailsData?.VendorId !== undefined) {
      dispatch(vendorNameListRequest(editorDetailsData?.VendorId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorDetailsData]);

  const closeModal = () => {
    if(isConfirmButtonLoading) return;
    setRateExplanantionValue('');
    setBlockVendor('');
    setValueRating(0);
    setReworkNote('');
    setPage('Vendor Rating');
    setValueRework('');
    setValueSendFile('');
    setPayVendor('');
    setSelectedVendor(null);
    setPayOriginalVendor(false);
    setHour(null);
    handleSubmitCloseModal();
  };
  const handleConfirm = () => {
    if(isConfirmButtonLoading) return;
    let params = {
      vendorId: selectedVendor !== null ? selectedVendor : editorDetailsData?.VendorId,
      originalVendorId: editorDetailsData?.VendorId,
      userId: userInfo?.UserId,
      audioFileId: id,
      submittedOn: new Date(),
      currentDate: formatDateWithMilliseconds(),
      rating: valueRating,
      explanation: rateExplanantionValue,
      isBlockVendor: blockVendor === 'yes' ? true : false,
      isRework: valueRework === 'yes' ? true : false,
      isOriginalVendor: valueSendFile === 'OriginalVendor' ? true : false,
      isPayOriginal: payOriginalVendor,
      isVendorRejectPay: payVendor !== 'yes' || vendorDetailData?.vendorType === '1' ? true : false,
      reworkHours: parseInt(hour, 10) || 0,
      reworkNote: reworkNote || '',
      vendorRate: vendorDetailData?.VendorRate || 0,
      payment:
        payVendor === 'false' || vendorDetailData?.vendorType === '1' || payOriginalVendor === false
          ? 0
          : vendorDetailData?.VendorPayment || 0,
      transcriptJSON: transcriptData ? JSON.stringify({ data: transcriptData }) : JSON.stringify({ data: [] }),
    };
    dispatch(ProofreaderRoleEditorSubmitRequest(params));
    if (valueRework === 'yes') {
      trackEvent('rework_file', {
        file_id: id,
        file_name: editorDetailsData?.UserFileName,
      });
    } else {
      trackEvent('file_submitted', {
        file_id: id,
        file_name: editorDetailsData?.UserFileName,
      });
    }
    setFilterApiCall(true);
  };

  const formatDueDate = (dateString) => {
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', options).replace(',', ' at');
  };

  useEffect(() => {
    if (hour?.length === 0) {
      setHour(null);
    }
  }, [hour]);

  return (
    <Modal
      show={submitShow}
      size="lg"
      onHide={closeModal}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      dialogClassName="custom-modal"
    >

      <div className="preview-dropdown-header py-3">
        <span className="fnt-20 fw-medium">
          {page === 'NewVendor'
            ? valueSendFile === 'NewVendor'
              ? 'New Vendor'
              : 'Original Vendor'
            : page === 'LegalInfo'
              ? 'Submit to Client'
              : page}
        </span>
        <HighlightOffRoundedIcon
          onClick={closeModal}
          className="cursor-pointer opacity-50"
          style={{ height: '28px', width: '28px' }}
        />
      </div>
      <div className="preview-dropdown-body" style={{ maxHeight: '80vh', overflow: 'auto', padding: '10px 20px' }}>
        {page === 'Vendor Rating' && (
          <div className="row">
            <div className="col-9">
              <span className="fnt-17 fw-medium d-flex align-items-center">
                Vendor Name :{' '}
                {vendorDetailData?.ProfilePicture || userInfo?.ProfilePicture ? (
                  <Avatar
                    alt="image"
                    src={
                      vendorDetailData?.ProfilePicture
                        ? `${VENDOR_IMAGE_URL}/${vendorDetailData?.ProfilePicture}`
                        : `${IMAGE_URL}/${userInfo?.ProfilePicture}`
                    }
                    className="submit-profile-avatar mx-3"
                  />
                ) : (
                  <Avatar alt="image" className="submit-profile-avatar mx-3" />
                )}{' '}
                <span className="fnt-16 fw-medium">
                  {vendorDetailData?.Name !== null
                    ? vendorDetailData?.Name
                    : capitalizeFirstLetter(
                      ((userInfo?.FirstName !== null && userInfo?.FileName !== 'null') === false
                        ? ''
                        : userInfo?.FirstName) +
                      ' ' +
                      ((userInfo?.LastName !== null && userInfo?.LastName !== 'null') === false
                        ? ''
                        : userInfo?.LastName),
                    )}
                </span>
              </span>
            </div>
            <div className="col-3 text-end">
              <span>
                <StarBorderRoundedIcon style={{ fontSize: '38px', color: '#fad85d' }} />{' '}
                {vendorDetailData?.AverageRating || 0} / 5
              </span>
            </div>
            <div className="col-12 pt-4 d-flex justify-content-between">
              <span
                className="fnt-14"
                style={{
                  border: '1px solid gray',
                  padding: '6px 10px',
                  backgroundColor: '#efefef',
                  borderRadius: '4px',
                }}
              >
                Submission Ratio : {vendorDetailData?.OnTimeSubmission || 0}%
              </span>
              {vendorDetailData?.LevelName && (
                <span
                  className="fnt-14"
                  style={{
                    border: '1px solid gray',
                    padding: '6px 10px',
                    backgroundColor: '#efefef',
                    borderRadius: '4px',
                  }}
                >
                  {vendorDetailData?.LevelName}
                </span>
              )}
              <span
                className="fnt-14"
                style={{
                  border: '1px solid gray',
                  padding: '6px 10px',
                  backgroundColor: '#efefef',
                  borderRadius: '4px',
                }}
              >
                Commitment Ratio : {vendorDetailData?.CommitmentRatio || 0}%
              </span>
            </div>
            <div className="col-12 pt-4">
              <span className="fnt-17 fw-medium d-flex align-items-center">
                Rate Transcript :{' '}
                <Rating
                  name="simple-controlled"
                  value={valueRating}
                  onChange={(event, newValue) => {
                    setBlockVendor('');
                    setValueRating(newValue);
                  }}
                  style={{ fontSize: '34px' }}
                />
              </span>
            </div>
            <div className="col-12 pt-4">
              <span className="fnt-17 fw-medium d-flex align-items-center">Rating Explanation : </span>
            </div>
            <div className="col-12 pt-2">
              <InputTextarea
                value={rateExplanantionValue}
                onChange={(e) => setRateExplanantionValue(e.target.value)}
                rows={5}
                style={{ width: '100%' }}
              />
            </div>
            {3 > valueRating && (
              <>
                <div className="col-12 pt-4">
                  <span className="fnt-17 fw-medium d-flex align-items-center">
                    Do you want to block this Vendor?
                  </span>
                </div>
                <div className="col-12 pt-3">
                  <div className="d-flex gap-3">
                    <div className="flex align-items-center">
                      <RadioButton
                        inputId="ingredient1"
                        value="yes"
                        onChange={(e) => {
                          setBlockVendor(e.value);
                          setReworkNote('');
                          setPage('Vendor Rating');
                          setValueRework('');
                          setValueSendFile('');
                          setPayVendor('');
                          setSelectedVendor(null);
                          setPayOriginalVendor(false);
                          setHour(null);
                        }}
                        checked={blockVendor === 'yes'}
                      />
                      <label htmlFor="ingredient1" className="ms-2">
                        Yes
                      </label>
                    </div>
                    <div className="flex align-items-center">
                      <RadioButton
                        inputId="ingredient2"
                        value="no"
                        onChange={(e) => {
                          setBlockVendor(e.value);
                          setReworkNote('');
                          setPage('Vendor Rating');
                          setValueRework('');
                          setValueSendFile('');
                          setPayVendor('');
                          setSelectedVendor(null);
                          setPayOriginalVendor(false);
                          setHour(null);
                        }}
                        checked={blockVendor === 'no'}
                      />
                      <label htmlFor="ingredient2" className="ms-2">
                        No
                      </label>
                    </div>
                  </div>
                </div>
              </>
            )}
            <div className="col-12 text-center pt-5 pb-4">
              <Button
                disabled={
                  valueRating === 0 ||
                  rateExplanantionValue === '' ||
                  (3 > valueRating ? !(blockVendor === 'yes' || blockVendor === 'no') : blockVendor !== '')
                }
                onClick={() => setPage('Rework')}
              >
                Next
              </Button>
            </div>
          </div>
        )}
        {page === 'Rework' && (
          <div className="row">
            <div className="col-12 pt-4">
              <span className="fnt-17 fw-medium d-flex align-items-center">
                Do you want to send this file for Rework?
              </span>
            </div>
            <div className="col-12 pt-3">
              <div className="d-flex gap-3">
                <div className="flex align-items-center">
                  <RadioButton
                    inputId="ingredient1"
                    value="yes"
                    onChange={(e) => {
                      setValueRework(e.value);
                      setReworkNote('');
                      setValueSendFile(blockVendor === 'yes' ? 'NewVendor' : 'OriginalVendor');
                      setPayVendor('');
                      setSelectedVendor(null);
                      setPayOriginalVendor(false);
                      setHour(null);
                    }}
                    checked={valueRework === 'yes'}
                  />
                  <label htmlFor="ingredient1" className="ms-2">
                    Yes
                  </label>
                </div>
                <div className="flex align-items-center">
                  <RadioButton
                    inputId="ingredient2"
                    value="no"
                    onChange={(e) => {
                      setValueRework(e.value);
                      setValueSendFile('');
                      setReworkNote('');
                      setPayVendor('');
                      setSelectedVendor(null);
                      setPayOriginalVendor(false);
                      setHour(null);
                    }}
                    checked={valueRework === 'no'}
                  />
                  <label htmlFor="ingredient2" className="ms-2">
                    No
                  </label>
                </div>
              </div>
            </div>
            {valueRework === 'yes' && (
              <div className="row">
                {valueRework === 'yes' && (
                  <>
                    <div className="col-12 pt-4">
                      <span className="fnt-17 fw-medium d-flex align-items-center">Send this file to</span>
                    </div>
                    <div className="col-12 pt-3">
                      <div className="d-flex gap-3">
                        <div className="flex align-items-center">
                          <RadioButton
                            inputId="ingredient3"
                            value="OriginalVendor"
                            onChange={(e) => {
                              setValueSendFile(e.value);
                              setReworkNote('');
                              setPayVendor('');
                              setSelectedVendor(null);
                              setPayOriginalVendor(false);
                            }}
                            checked={valueSendFile === 'OriginalVendor'}
                            disabled={blockVendor === 'yes'}
                          />
                          <label htmlFor="ingredient1" className="ms-2">
                            Previous Vendor
                          </label>
                        </div>
                        <div className="flex align-items-center">
                          <RadioButton
                            inputId="ingredient4"
                            value="NewVendor"
                            onChange={(e) => {
                              setValueSendFile(e.value);
                              setReworkNote('');
                              setPayVendor('');
                              setSelectedVendor(null);
                              setPayOriginalVendor(false);
                            }}
                            checked={valueSendFile === 'NewVendor'}
                          />
                          <label htmlFor="ingredient3" className="ms-2">
                            New Vendor
                          </label>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {valueSendFile === 'NewVendor' && (
                  <>
                    <div className="col-5 pt-4">
                      <VendorSelectWithSearch
                        selectedVendor={selectedVendor}
                        setSelectedVendor={setSelectedVendor}
                        setSelectedNewVendor={setSelectedNewVendor}
                      />
                    </div>
                    <div className="col-2 pt-4"></div>
                    {vendorDetailData?.vendorType !== '1' && blockVendor !== 'yes' && (
                      <div className="col-5 pt-4">
                        <Checkbox
                          inputId="ingredient12"
                          onChange={(e) => setPayOriginalVendor(e.checked)}
                          checked={payOriginalVendor}
                        />
                        <label htmlFor="ingredient12" className="ms-2">
                          Pay Previous Vendor
                        </label>
                      </div>
                    )}
                  </>
                )}
                <div className="col-12 pt-4">
                  <span className="fnt-17 fw-medium d-flex align-items-center">Rework Notes : </span>
                </div>
                <div className="col-12 pt-2">
                  <InputTextarea
                    value={reworkNote}
                    onChange={(e) => setReworkNote(e.target.value)}
                    placeholder="Write your rework instructions to the vendor"
                    rows={5}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className="col-12 pt-4" style={{ color: 'red' }}>
                  {editorDetailsData?.DueDate ? (
                    <>Client Due Date is {formatDueDate(editorDetailsData.DueDate)}</>
                  ) : (
                    <>Client Due Date is not set</>
                  )}
                </div>
                <div className="col-12 pt-2">
                  <span className="fnt-17 fw-medium d-flex align-items-center">Rework Hour : </span>
                </div>
                <div className="col-5 pt-2">
                  <input
                    type="number"
                    value={hour}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      // Only update if the value is within the 0-99 range or if it's an empty value (to allow clearing the input)
                      if ((value >= 1 && value <= 99) || e.target.value === '') {
                        setHour(e.target.value); // Update with the valid value or empty string
                      }
                    }}
                    min="1"
                    max="99"
                    style={{
                      width: '100%',
                      height: '40px',
                      padding: '0 10px',
                    }}
                  />
                </div>
              </div>
            )}
            <div className="col-12 text-center pt-5 pb-4">
              <Button onClick={() => setPage('Vendor Rating')} className="me-2">
                Back
              </Button>
              <Button
                disabled={
                  valueRework === 'yes'
                    ? !(
                      valueSendFile !== '' &&
                      reworkNote !== '' &&
                      hour !== null &&
                      (valueSendFile === 'NewVendor' ? selectedVendor !== null : selectedVendor == null)
                    )
                    : valueRework === ''
                }
                onClick={() => {
                  if (vendorDetailData?.vendorType === '1') {
                    setPage('Summary');
                  } else {
                    setPage(valueRework === 'yes' ? 'Summary' : 'Payment');
                  }
                }}
              >
                Next
              </Button>
            </div>
          </div>
        )}
        {page === 'Payment' && (
          <div className="row">
            {valueRework === 'no' && (
              <>
                <div className="col-12 pt-4">
                  <span className="fnt-17 fw-medium d-flex align-items-center">Do you want to pay the Vendor?</span>
                </div>
                <div className="col-12 pt-3">
                  <div className="d-flex gap-3">
                    <div className="flex align-items-center">
                      <RadioButton
                        inputId="ingredient3"
                        value="yes"
                        onChange={(e) => setPayVendor(e.value)}
                        checked={payVendor === 'yes'}
                      />
                      <label htmlFor="ingredient1" className="ms-2">
                        Yes
                      </label>
                    </div>
                    <div className="flex align-items-center">
                      <RadioButton
                        inputId="ingredient4"
                        value="no"
                        onChange={(e) => setPayVendor(e.value)}
                        checked={payVendor === 'no'}
                      />
                      <label htmlFor="ingredient2" className="ms-2">
                        No
                      </label>
                    </div>
                  </div>
                </div>
                {payVendor === 'yes' && (
                  <>
                    <div className="col-6 pt-4">
                      <span className="fnt-17 fw-medium me-3">Payment Details :</span>
                    </div>
                    <div className="col-6 pt-4 text-end">
                      <span
                        style={{
                          background: '#efe3e3',
                          padding: '8px 12px',
                          borderRadius: '4px',
                        }}
                      >
                        <span className="fnt-16 fw-medium me-3">Vendor Rate :</span>$
                        {vendorDetailData?.VendorRate || 0} /min
                      </span>
                    </div>
                    <div className="col-6 pt-4 d-flex d-flex align-items-center">
                      <span className="fnt-17 fw-medium me-3">File Length :</span>
                      {vendorDetailData?.Duration}
                    </div>
                    <div className="col-6 pt-4 d-flex d-flex align-items-center">
                      <span className="fnt-17 fw-medium me-3">Vendor Name :</span>
                      {vendorDetailData?.Name !== null
                        ? vendorDetailData?.Name
                        : capitalizeFirstLetter(
                          ((userInfo?.FirstName !== null && userInfo?.FileName !== 'null') === false
                            ? ''
                            : userInfo?.FirstName) +
                          ' ' +
                          ((userInfo?.LastName !== null && userInfo?.LastName !== 'null') === false
                            ? ''
                            : userInfo?.LastName),
                        )}
                    </div>
                    <div className="col-6 pt-4 d-flex d-flex align-items-center">
                      <span className="fnt-17 fw-medium me-3">Vendor Payment :</span>$
                      {vendorDetailData?.VendorPayment || 0}
                    </div>
                    <div className="col-6 pt-4 d-flex d-flex align-items-center">
                      <span className="fnt-17 fw-medium me-3">File Name :</span>
                      <CustomTooltips title={editorDetailsData?.UserFileName} placement="top">
                        {truncateString(editorDetailsData?.UserFileName, 18)}
                      </CustomTooltips>
                    </div>
                  </>
                )}
                <div className="col-12 text-center pt-5 pb-4">
                  <Button onClick={() => setPage('Rework')} className="me-2">
                    Back
                  </Button>
                  <Button disabled={payVendor === ''} onClick={() => setPage('Summary')}>
                    Next
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
        {page === 'Summary' && (
          <div className="row">
            <div className="col-9">
              <span className="fnt-17 fw-medium d-flex align-items-center">
                Vendor Name :{' '}
                <Avatar
                  alt="image"
                  src={
                    vendorDetailData?.ProfilePicture
                      ? `${VENDOR_IMAGE_URL}/${vendorDetailData?.ProfilePicture}`
                      : `${IMAGE_URL}/${userInfo?.ProfilePicture}`
                  }
                  className="submit-profile-avatar mx-3"
                />{' '}
                <span className="fnt-16 fw-medium">{vendorDetailData?.Name}</span>
              </span>
            </div>
            <div className="col-3 text-end">
              <span>
                <StarBorderRoundedIcon style={{ fontSize: '38px', color: '#fad85d' }} /> {valueRating || 0} / 5
              </span>
            </div>
            <div className="col-12 pt-4">
              <span className="fnt-17 fw-medium d-flex align-items-center">Rating Feedback :</span>
            </div>
            <div className="col-12 pt-2">{rateExplanantionValue}</div>
            <div className="col-12 pt-4 bb-1"></div>
            <div className="col-6 pt-4 d-flex d-flex align-items-center">
              <span className="fnt-17 fw-medium me-3">Rework :</span>
              {valueRework === 'yes' ? 'Yes' : 'No'}
            </div>
            {valueRework === 'yes' && (
              <>
                <div className="col-6 pt-4 d-flex d-flex align-items-center">
                  <span className="fnt-17 fw-medium me-3">Rework to :</span>
                  {selectedVendor !== null ? (
                    <>
                      <div className="position-relative">
                        <Avatar
                          alt="image"
                          src={
                            selectedNewVendor?.ProfilePicture !== null && selectedNewVendor?.ProfilePicture
                              ? `${VENDOR_IMAGE_URL}/${selectedNewVendor?.ProfilePicture}`
                              : null
                          }
                          className="submit-profile-avatar mx-3"
                        />
                        <span
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            right: '14px',
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: selectedNewVendor?.isClaimed === 1 ? 'red' : 'green',
                            border: '2px solid white',
                          }}
                        />
                      </div>{' '}
                      {selectedNewVendor?.Name}{' '}
                      {selectedNewVendor?.IsInHouse === 1 && (
                        <HomeOutlinedIcon
                          style={{
                            color: '#2b2b67',
                            fontSize: '30px',
                            marginLeft: '6px',
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <div className="position-relative">
                        <Avatar
                          alt="image"
                          src={
                            vendorDetailData?.ProfilePicture
                              ? `${VENDOR_IMAGE_URL}/${vendorDetailData?.ProfilePicture}`
                              : `${IMAGE_URL}/${userInfo?.ProfilePicture}`
                          }
                          className="submit-profile-avatar mx-3"
                        />
                      </div>{' '}
                      {vendorDetailData?.Name !== null
                        ? vendorDetailData?.Name
                        : capitalizeFirstLetter(
                          ((userInfo?.FirstName !== null && userInfo?.FileName !== 'null') === false
                            ? ''
                            : userInfo?.FirstName) +
                          ' ' +
                          ((userInfo?.LastName !== null && userInfo?.LastName !== 'null') === false
                            ? ''
                            : userInfo?.LastName),
                        )}{' '}
                    </>
                  )}
                </div>
                <div className="col-6 pt-4 d-flex d-flex align-items-center">
                  <span className="fnt-17 fw-medium me-3">Rework hour :</span>
                  {hour}
                </div>
                <div className="col-6 pt-4 d-flex d-flex align-items-center">
                  <span className="fnt-17 fw-medium me-3">Client Due Date :</span>
                  {moment(editorDetailsData?.DueDate).format('DD/MM/YYYY')}
                </div>
                <div className="col-12 pt-4">
                  <span className="fnt-17 fw-medium d-flex align-items-center">Rework Notes :</span>
                </div>
                <div className="col-12 pt-2">{reworkNote}</div>
              </>
            )}
            {vendorDetailData?.vendorType !== '1' && (
              <>
                {valueSendFile === 'NewVendor' && valueRework === 'yes' && blockVendor !== 'yes' && (
                  <>
                    <div className="col-12 pt-4 bb-1"></div>
                    <div className="col-12 pt-4">
                      <span className="fnt-17 fw-medium me-3">Payment Details :</span>
                    </div>
                    <div className="col-6 pt-4 d-flex d-flex align-items-center">
                      <span className="fnt-17 fw-medium me-3">Vendor Rate :</span>${vendorDetailData?.VendorRate || 0}{' '}
                      /min
                    </div>
                    <div className="col-6 pt-4 d-flex d-flex align-items-center">
                      <span className="fnt-17 fw-medium me-3">Vendor Payment :</span>$
                      {vendorDetailData?.VendorPayment || 0}
                    </div>
                  </>
                )}
                {payVendor === 'yes' && valueRework === 'no' && (
                  <>
                    <div className="col-12 pt-4 bb-1"></div>
                    <div className="col-12 pt-4">
                      <span className="fnt-17 fw-medium me-3">Payment Details :</span>
                    </div>
                    <div className="col-6 pt-4 d-flex d-flex align-items-center">
                      <span className="fnt-17 fw-medium me-3">Vendor Rate :</span>${vendorDetailData?.VendorRate || 0}{' '}
                      /min
                    </div>
                    <div className="col-6 pt-4 d-flex d-flex align-items-center">
                      <span className="fnt-17 fw-medium me-3">Vendor Payment :</span>$
                      {vendorDetailData?.VendorPayment || 0}
                    </div>
                  </>
                )}
                {payVendor === 'no' && valueRework === 'no' && (
                  <>
                    <div className="col-12 pt-4 bb-1"></div>
                    <div className="col-12 pt-4">
                      <span className="fnt-17 fw-medium me-3">Payment Details :</span>
                    </div>
                    <div className="col-12 pt-4 d-flex d-flex align-items-center">
                      <span className="fnt-17 fw-medium me-3">Note :</span>
                      Vendor Payment is Rejected for this File.
                    </div>
                  </>
                )}
                {blockVendor === 'yes' && valueRework === 'yes' && (
                  <>
                    <div className="col-12 pt-4 bb-1"></div>
                    <div className="col-12 pt-4">
                      <span className="fnt-17 fw-medium me-3">Payment Details :</span>
                    </div>
                    <div className="col-12 pt-4 d-flex d-flex align-items-center">
                      <span className="fnt-17 fw-medium me-3">Note :</span>
                      Vendor Payment is Rejected for this File.
                    </div>
                  </>
                )}
              </>
            )}
            {vendorDetailData?.vendorType === '1' && (
              <>
                <div className="col-12 pt-4 bb-1"></div>
                <div className="col-12 pt-4">
                  <span className="fnt-17 fw-medium me-3">Payment Details :</span>
                </div>
                <div className="col-12 pt-4 d-flex d-flex align-items-center">
                  <span className="fnt-17 fw-medium me-3">Note :</span>
                  Vendor Payment is Rejected for this File.
                </div>
              </>
            )}
            <div className="col-12 text-center pt-5 pb-4">
              <Button
                onClick={() => {
                  if(isConfirmButtonLoading) return;
                  if (vendorDetailData?.vendorType === '1') {
                    setPage('Rework');
                  } else {
                    setPage(valueRework === 'yes' ? 'Rework' : 'Payment');
                  }
                }}
                disabled={isConfirmButtonLoading}
                className="me-2"
              >
                Back
              </Button>
              <Button
                onClick={() => {
                  if(isConfirmButtonLoading) return;
                  if (
                    editorDetailsData.TemplateTrans !== 0 &&
                    editorDetailsData.TemplateTrans !== null &&
                    legalTemplateFormFieldDetails?.isDownloadFromPR &&
                    valueRework === 'no'
                  ) {
                    setPage('LegalInfo');
                  } else {
                    handleConfirm();
                  }
                }}
                disabled={isConfirmButtonLoading}
              >
                {`${editorDetailsData.TemplateTrans !== 0 &&
                  editorDetailsData.TemplateTrans !== null &&
                  legalTemplateFormFieldDetails?.isDownloadFromPR && valueRework === 'no'
                  ? 'Next'
                  : 'Confirm'
                  }`}
              </Button>
            </div>
          </div>
        )}
        {page === 'LegalInfo' && (
          <div className="row" style={{ overflow: 'auto', maxHeight: "70vh" }}>
            <div className="col-12">
              <span
                className="fnt-14 fw-medium cursor-pointer text-decoration-underline text-primary"
                onClick={() =>
                  dispatch(
                    downloadLegalDocumentRequest({
                      audioFileId: editorDetailsData?.Id,
                      template: editorDetailsData?.TemplateTrans,
                      fileName: editorDetailsData?.UserFileName.replace(/\.\w+$/, ''),
                      CGAType: editorDetailsData?.CGAType,
                    }),
                  )
                }
              >
                Download Transcript
              </span>
            </div>
            <div className="col-12 pt-4">
              <span className="fnt-17 fw-medium">
                Click on the "Choose File" button to upload transcript file:
              </span>
              <input
                type="file"
                className="w-100"
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: '4px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
                onChange={(e) =>
                  dispatch(
                    uploadLegalDocumentRequest({
                      file: e.target.files[0],
                      audioFileId: editorDetailsData?.Id,
                    }),
                  )
                }
              />
            </div>
            <div className="col-12 d-flex flex-column pt-4">
              <span className="fnt-17 fw-medium">Are you sure to submit this to client?</span>
              {editorDetailsData.IsInHouse === 1 && (
                <span className="fnt-16 fw-medium mt-2">Note: This is an Inhouse vendor.</span>
              )}
            </div>
            <div className="col-12 d-flex flex-column gap-1 pt-2">
              {fieldsData.map((field) => {
                const value = initialValues[field.DisplayName];
                if (field.FieldType === 'Group' && Array.isArray(field.Children)) {
                  const shouldRenderGroup = field.Children.some((groupField) => {
                    const groupValue = initialValues[groupField.DisplayName] || [];
                    return groupValue.length > 0;
                  });

                  if (!shouldRenderGroup) return null;

                  return (
                    <div>
                      <CustomTypography size="medium" weight="bold">
                        {field.DisplayName}
                      </CustomTypography>
                      <div className='ps-3'>
                        {field.Children.map((groupField) => {
                          const groupValue = initialValues[groupField.DisplayName] || [];
                          return (
                            groupValue.length > 0 && (
                              <div key={groupField.DisplayName} style={{ marginBottom: '10px' }}>
                                <CustomTypography size="medium" weight="medium">
                                  {groupField.DisplayName}
                                </CustomTypography>
                                {(groupValue || []).map((entry, idx) => (
                                  <div key={idx} className="d-flex gap-1">
                                    <CustomTypography size="smallMedium" weight="medium">
                                      {idx + 1}.
                                    </CustomTypography>
                                    <div>
                                      {(Object.entries(entry) || []).map(([label, val]) => (
                                        <div key={label}>
                                          <span style={{ color: 'gray' }}>
                                            {label}:&nbsp;
                                            {val || '--'}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )
                          );
                        })}
                      </div>
                    </div>
                  )
                }
                return (
                  <div key={field.DisplayName}>
                    <span style={{ color: 'gray' }}>
                      {field.DisplayName}:&nbsp;
                      {value || '--'}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="col-12 text-center pt-4 pb-4">
              <Button
                onClick={() => {
                  if(isConfirmButtonLoading) return;
                  setPage('Summary');
                  dispatch(uploadLegalDocumentFailure(null));
                }}
                disabled={isConfirmButtonLoading}
                className="me-2"
              >
                Back
              </Button>
              <Button disabled={!isUploadedLegalDocument || isConfirmButtonLoading} onClick={handleConfirm}>
                Confirm
              </Button>
            </div>
          </div>
        )}
      </div>

    </Modal>
  );
};

export default ProofreaderSubmitModal;
